import React from 'react'

interface IJumpToVideoCard {
  videoTitle: string
  stepsCovered: number[]
  onJumpToVideo: () => void
}

/**
 * A simplified card component shown for steps that are covered by a multi-step video
 * from a previous step. Instead of embedding the video again, it shows a "Jump to video" link.
 * This matches the Figma design specification.
 */
export const JumpToVideoCard: React.FC<IJumpToVideoCard> = ({
  videoTitle,
  stepsCovered,
  onJumpToVideo,
}) => {
  // Format steps range (e.g., "STEPS 1 → 3")
  const formatStepsRange = (steps: number[]) => {
    if (steps.length === 0) return ''
    if (steps.length === 1) return `STEP ${steps[0]}`

    const sortedSteps = [...steps].sort((a, b) => a - b)
    return `STEPS ${sortedSteps[0]} → ${sortedSteps[sortedSteps.length - 1]}`
  }

  const stepsLabel = formatStepsRange(stepsCovered)

  return (
    <div className="bg-[#F5F3FF] border border-[#E9D5FF] rounded-lg p-4">
      <div className="flex items-start gap-3">
        {/* Stacked layers icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-[#705AF8] rounded-lg flex items-center justify-center">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="8" fill="#705AF8" />
              <path
                d="M15.3571 18.125L11.875 20L15.3571 21.875M15.3571 18.125L20 20.625L24.6429 18.125M15.3571 18.125L11.875 16.25L20 11.875L28.125 16.25L24.6429 18.125M24.6429 18.125L28.125 20L24.6429 21.875M24.6429 21.875L28.125 23.75L20 28.125L11.875 23.75L15.3571 21.875M24.6429 21.875L20 24.375L15.3571 21.875"
                stroke="white"
                stroke-width="1.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Badge */}
          {stepsCovered.length > 1 && (
            <div className="inline-flex mb-2">
              <div className="bg-[#705AF8] text-white px-2.5 py-0.5 rounded-lg text-[11px] font-bold font-inter">
                {stepsLabel}
              </div>
            </div>
          )}

          {/* Video title */}
          <h3 className="text-sm font-semibold text-[#705AF8] font-inter mb-1">{videoTitle}</h3>

          {/* Description */}
          <p className="text-[13px] text-[#705AF8] font-inter mb-2">
            This comprehensive video covers{' '}
            {stepsCovered.length > 1
              ? `steps ${stepsCovered[0]} through ${stepsCovered[stepsCovered.length - 1]}`
              : `step ${stepsCovered[0]}`}
          </p>

          {/* Steps visualization bars */}
          <div className="flex gap-1 mb-3">
            {stepsCovered.map((step) => (
              <div key={step} className="h-1 flex-1 bg-[#705AF8] rounded-full" />
            ))}
          </div>

          {/* Jump to video link */}
          <button
            onClick={onJumpToVideo}
            className="flex items-center gap-1 text-xs font-medium text-[#705AF8] hover:text-[#705AF8] transition-colors"
          >
            Jump to video
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.33594 8H12.6693"
                stroke="#705AF8"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 3.33337L12.6667 8.00004L8 12.6667"
                stroke="#705AF8"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
